// Client-side conversation persistence so the chat survives closing the bubble
// and reloading the page — the visitor returns to exactly where they left off.
// Scoped per firm, with a TTL so stale conversations don't resurrect forever.
import type { CloseReason } from "./transport";
import type { Message } from "./widget";

export type PersistedChat = {
  sessionId: string | null;
  messages: Message[];
  ended: boolean;
  /** Why the session ended — restores the right ended-sheet copy on reopen. */
  close_reason?: CloseReason | null;
  ts: number;
};

const TTL_MS = 6 * 60 * 60 * 1000; // 6h — live (unfinished) conversations
// Single session per visitor: once a session ends, the ended marker (and the
// ended transcript) persist for 24h so reopening the widget shows the closed
// conversation — never a fresh chat or the pre-chat form. The 24h expiry is
// the deliberate escape hatch: tomorrow's genuinely new inquiry isn't blocked.
export const SESSION_ENDED_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const key = (firm: string) => `acceltra_chat:${firm}`;

// Shared read: live entries expire at TTL_MS; ended entries live the full 24h
// window (their `ts` is anchored at end time — see markSessionEnded).
function readChat(firm: string): PersistedChat | null {
  try {
    const raw = localStorage.getItem(key(firm));
    if (!raw) return null;
    const p = JSON.parse(raw) as PersistedChat;
    const ttl = p?.ended ? SESSION_ENDED_TTL_MS : TTL_MS;
    if (!p || typeof p.ts !== "number" || Date.now() - p.ts > ttl) {
      localStorage.removeItem(key(firm));
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

/** An in-progress (live) chat to resume, or null. Ended chats are never
 *  returned here — restore those via loadEndedSession. */
export function loadChat(firm: string): PersistedChat | null {
  const p = readChat(firm);
  return p && !p.ended ? p : null;
}

export function saveChat(firm: string, p: Omit<PersistedChat, "ts">): void {
  try {
    localStorage.setItem(key(firm), JSON.stringify({ ...p, ts: Date.now() }));
  } catch {
    /* storage full / disabled — non-fatal */
  }
}

export function clearChat(firm: string): void {
  try {
    localStorage.removeItem(key(firm));
  } catch {
    /* ignore */
  }
}

/** True if there's an in-progress text chat the visitor has actually engaged
 *  with (used to decide whether to warn before they leave the page). */
export function hasActiveChat(firm: string): boolean {
  const p = loadChat(firm);
  return !!p && !p.ended && p.messages.some((m) => m.role === "user");
}

// ── Single-session-per-visitor lock ─────────────────────────────────────────
// Written when a chat session ends (any reason). While present, the widget
// mounts straight into the ended view of the prior conversation and never
// starts a new session or shows the pre-chat form. Kept SEPARATE from the
// (bigger) transcript entry so the lock survives even if that write failed.
// Key style follows the theme cache convention (`acceltra_theme:<firm>`).

type EndedMarker = { ts: number; close_reason: CloseReason };

const endedKey = (firm: string) => `acceltra_chat_ended:${firm}`;

function readEndedMarker(firm: string): EndedMarker | null {
  try {
    const raw = localStorage.getItem(endedKey(firm));
    if (!raw) return null;
    const p = JSON.parse(raw) as EndedMarker;
    if (!p || typeof p.ts !== "number" || Date.now() - p.ts > SESSION_ENDED_TTL_MS) {
      localStorage.removeItem(endedKey(firm));
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

/** Record that this visitor's session ended. Write-once per end: an existing
 *  unexpired marker keeps its ORIGINAL timestamp, so re-rendering the ended
 *  view can never extend the 24h lock. */
export function markSessionEnded(firm: string, reason: CloseReason): void {
  try {
    if (readEndedMarker(firm)) return;
    localStorage.setItem(
      endedKey(firm),
      JSON.stringify({ ts: Date.now(), close_reason: reason }),
    );
  } catch {
    /* storage full / disabled — non-fatal */
  }
}

export type EndedSession = {
  close_reason: CloseReason;
  /** The ended transcript, if it survived in storage ([] otherwise — the
   *  caller renders the ended sheet over a short recap line instead). */
  messages: Message[];
};

/** The visitor's ended session within the 24h window, assembled from the
 *  marker and/or the persisted ended transcript (either alone suffices —
 *  best-effort storage means one write can land without the other). */
export function loadEndedSession(firm: string): EndedSession | null {
  const marker = readEndedMarker(firm);
  const chat = readChat(firm);
  const endedChat = chat?.ended ? chat : null;
  if (!marker && !endedChat) return null;
  return {
    close_reason: endedChat?.close_reason ?? marker?.close_reason ?? "callback",
    messages: endedChat?.messages ?? [],
  };
}

/** True while the single-session lock is active (suppresses the nudge). */
export function hasEndedSession(firm: string): boolean {
  return loadEndedSession(firm) !== null;
}

// ── Pre-chat form persistence ────────────────────────────────────────────────
// Contact prefill (name/phone only — never the inquiry) so a returning visitor
// doesn't retype, and a per-tab "captured" flag so reopening after a captured
// outcome shows the confirmation instead of inviting a duplicate submission.
// All best-effort: storage being blocked just means no prefill.

export type PrechatContact = {
  first_name: string;
  last_name: string | null;
  phone: string;
};

const PRECHAT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30d
const prechatKey = (firm: string) => `acc_prechat:${firm}`;
const capturedKey = (firm: string) => `acc_prechat_captured:${firm}`;

export function loadPrechatContact(firm: string): PrechatContact | null {
  try {
    const raw = localStorage.getItem(prechatKey(firm));
    if (!raw) return null;
    const p = JSON.parse(raw) as PrechatContact & { ts: number };
    if (!p || typeof p.ts !== "number" || Date.now() - p.ts > PRECHAT_TTL_MS) {
      localStorage.removeItem(prechatKey(firm));
      return null;
    }
    return { first_name: p.first_name, last_name: p.last_name, phone: p.phone };
  } catch {
    return null;
  }
}

export function savePrechatContact(firm: string, c: PrechatContact): void {
  try {
    localStorage.setItem(prechatKey(firm), JSON.stringify({ ...c, ts: Date.now() }));
  } catch {
    /* storage full / disabled — non-fatal */
  }
}

/** Same-tab flag: this visitor's submission was captured without a chat. */
export function wasCapturedThisTab(firm: string): boolean {
  try {
    return sessionStorage.getItem(capturedKey(firm)) === "1";
  } catch {
    return false;
  }
}

export function markCapturedThisTab(firm: string): void {
  try {
    sessionStorage.setItem(capturedKey(firm), "1");
  } catch {
    /* ignore */
  }
}

/** Explicit visitor intent to submit again ("Send another message"). */
export function clearCapturedThisTab(firm: string): void {
  try {
    sessionStorage.removeItem(capturedKey(firm));
  } catch {
    /* ignore */
  }
}

// A prechat submit that is still in flight when the bubble is closed keeps
// running (the lead matters more than the UI). This marker lets a REMOUNTED
// ChatWindow adopt that orphan — holding "connecting" and polling for its
// terminal state — instead of landing on a fresh welcome that invites a
// duplicate submission. TTL bounds the wait if the old handler died silently.
const INFLIGHT_TTL_MS = 35_000;
const inflightKey = (firm: string) => `acc_prechat_inflight:${firm}`;

export function markSubmitInFlight(firm: string): void {
  try {
    sessionStorage.setItem(inflightKey(firm), String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearSubmitInFlight(firm: string): void {
  try {
    sessionStorage.removeItem(inflightKey(firm));
  } catch {
    /* ignore */
  }
}

export function isSubmitInFlight(firm: string): boolean {
  try {
    const raw = sessionStorage.getItem(inflightKey(firm));
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts) || Date.now() - ts > INFLIGHT_TTL_MS) {
      sessionStorage.removeItem(inflightKey(firm));
      return false;
    }
    return true;
  } catch {
    return false;
  }
}
